<template>
  <div class="weekly-report-container">
    <!-- 权限检查和加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <p>正在检查权限...</p>
    </div>
    
    <!-- 非领队提示 -->
    <div v-else-if="!isLeader" class="no-permission">
      <i class="el-icon-warning"></i>
      <h3>访问受限</h3>
      <p>您不是批次领队，无权限访问周报功能</p>
      <el-button type="primary" @click="goBack">返回</el-button>
    </div>
    
    <!-- 领队周报管理界面 -->
    <div v-else class="report-management">
      <!-- 页面头部 -->
      <div class="page-header">
        <div class="header-content">
          <p class="batch-info">批次：{{ batchInfo.batch_name }}</p>
        </div>
        <div class="header-actions">
          <el-button type="success" icon="el-icon-document" @click="goToMonthlyReportView">月报查看</el-button>
        </div>
      </div>

      <!-- 标签页 -->
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <!-- 填写周报 -->
        <el-tab-pane label="填写周报" name="create">
          <div class="create-report-section">
            <el-card shadow="hover">
              <div slot="header" class="card-header">
                <span><i class="el-icon-edit"></i> {{ currentWeek }} 周报</span>
                <div>
                  <el-button 
                    type="text" 
                    icon="el-icon-refresh"
                    @click="loadLastWeekData"
                    :loading="loadingHistory">
                    复制上期数据
                  </el-button>
                  <el-button 
                    type="text" 
                    icon="el-icon-plus"
                    @click="initializeStudentForms">
                    初始化学生列表
                  </el-button>
                </div>
              </div>
              
              <!-- 填写时间和要求提示 -->
              <div v-if="reportSettings" class="report-settings-info">
                <el-card shadow="hover" class="settings-card">
                  <div slot="header" class="settings-header">
                    <i class="el-icon-info-circle"></i> 填写须知
                  </div>
                  <div class="settings-content">
                    <div class="time-limit">
                      <el-row :gutter="10" type="flex" align="middle">
                        <el-col :span="4" :xs="6">
                          <span class="label"><i class="el-icon-date"></i> 填写时间：</span>
                        </el-col>
                        <el-col :span="14" :xs="12">
                          <span class="value">每周 {{ reportSettings.start_day }} 至周 {{ reportSettings.end_day }}</span>
                        </el-col>
                        <el-col :span="6" :xs="6">
                          <el-tag v-if="reportSettings.can_submit" type="success" effect="dark" size="small">当前可填写</el-tag>
                          <el-tag v-else type="danger" effect="dark" size="small">当前不可填写</el-tag>
                        </el-col>
                      </el-row>
                    </div>
                    <div class="requirements" v-if="reportSettings.requirements">
                      <el-row :gutter="10" class="requirement-row">
                        <el-col :span="4" :xs="6">
                          <span class="label"><i class="el-icon-document-checked"></i> 填写要求：</span>
                        </el-col>
                        <el-col :span="20" :xs="18">
                          <span class="value">{{ reportSettings.requirements }}</span>
                        </el-col>
                      </el-row>
                    </div>
                  </div>
                </el-card>
              </div>
              
              <!-- 学生列表表单 -->
              <div v-if="studentDetailsForm.length > 0">
                <div class="quick-overview">
                  <div class="overview-card">
                    <span class="overview-label">学生总数</span>
                    <strong>{{ studentDetailsForm.length }}</strong>
                  </div>
                  <div class="overview-card">
                    <span class="overview-label">已完成</span>
                    <strong>{{ completedStudentCount }}</strong>
                  </div>
                  <div class="overview-card warning">
                    <span class="overview-label">待完善必填</span>
                    <strong>{{ incompleteStudentCount }}</strong>
                  </div>
                  <div class="overview-card danger">
                    <span class="overview-label">异常停飞</span>
                    <strong>{{ abnormalStopCount }}</strong>
                  </div>
                </div>

                <div class="quick-fill-panel">
                  <div class="quick-fill-header">
                    <span><i class="el-icon-magic-stick"></i> 快捷填写</span>
                    <span class="quick-fill-tip">适合批量补同一训练基地、阶段和送训日期</span>
                  </div>
                  <div class="quick-fill-body">
                    <el-input
                      v-model="bulkFill.training_base"
                      placeholder="批量训练基地"
                      size="small"
                      clearable>
                    </el-input>
                    <el-select
                      v-model="bulkFill.training_stage"
                      placeholder="批量训练阶段"
                      size="small"
                      clearable>
                      <el-option
                        v-for="stage in trainingStages"
                        :key="stage.name"
                        :label="stage.name"
                        :value="stage.name">
                      </el-option>
                    </el-select>
                    <el-date-picker
                      v-model="bulkFill.flight_start_date"
                      type="date"
                      placeholder="批量送训日期"
                      size="small"
                      format="yyyy-MM-dd"
                      value-format="yyyy-MM-dd">
                    </el-date-picker>
                    <el-input
                      v-model="bulkFill.safety_situation"
                      type="textarea"
                      :rows="2"
                      placeholder="批量安全情况"
                      size="small"
                      maxlength="200"
                      show-word-limit>
                    </el-input>
                    <el-input
                      v-model="bulkFill.other_situation"
                      type="textarea"
                      :rows="2"
                      placeholder="批量其他情况"
                      size="small"
                      maxlength="200"
                      show-word-limit>
                    </el-input>
                    <div class="quick-fill-actions">
                      <el-button size="small" @click="applyBulkValues('empty')">填充空白项</el-button>
                      <el-button size="small" type="primary" plain @click="applyBulkValues('all')">覆盖全部</el-button>
                      <el-button size="small" type="text" @click="resetBulkFill">清空快捷填写</el-button>
                    </div>
                  </div>
                </div>

                <div class="student-forms-header">
                  <h4>学生周报详情 (共 {{ studentDetailsForm.length }} 名学生)</h4>
                  <el-button 
                    type="primary" 
                    size="small"
                    icon="el-icon-check"
                    @click="submitReport"
                    :loading="submitting"
                    :disabled="reportSettings && !reportSettings.can_submit">
                    提交周报
                  </el-button>
                  <el-tooltip v-if="reportSettings && !reportSettings.can_submit" effect="dark" content="当前不在允许填写的时间范围内" placement="top">
                    <i class="el-icon-warning-outline" style="color: #E6A23C; margin-left: 5px;"></i>
                  </el-tooltip>
                </div>
                
                <!-- 学生表单列表 -->
                <div class="student-forms-container">
                  <!-- Excel样式的表格 -->
                  <el-table 
                    :data="studentDetailsForm" 
                    border 
                    style="width: 100%"
                    :header-cell-style="{background:'#fafafa', color:'#606266', fontWeight:'bold'}"
                    max-height="600">
                    
                    <!-- 学号姓名 -->
                    <el-table-column label="学生信息" width="150" >
                      <template slot-scope="scope">
                        <div class="student-info-cell">
                          <div class="student-name">{{ scope.row.name }}</div>
                          <div class="student-number">{{ scope.row.student_number }}</div>
                          <div class="student-class">{{ scope.row.class_id }}</div>
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 训练基地 -->
                    <el-table-column label="训练基地" width="180">
                      <template slot-scope="scope">
                        <el-input 
                          v-model="scope.row.training_base" 
                          placeholder="请输入训练基地"
                          size="small"
                          :class="{'is-error': validationErrors[scope.$index] && validationErrors[scope.$index].training_base}"
                          clearable>
                        </el-input>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].training_base" class="error-text">
                          {{ validationErrors[scope.$index].training_base }}
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 训练阶段 -->
                    <el-table-column label="训练阶段" width="150">
                      <template slot-scope="scope">
                        <el-select 
                          v-model="scope.row.training_stage" 
                          placeholder="请选择"
                          size="small"
                          :class="{'is-error': validationErrors[scope.$index] && validationErrors[scope.$index].training_stage}"
                          style="width: 100%"
                          clearable>
                          <el-option
                            v-for="stage in trainingStages"
                            :key="stage.name"
                            :label="stage.name"
                            :value="stage.name">
                          </el-option>
                        </el-select>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].training_stage" class="error-text">
                          {{ validationErrors[scope.$index].training_stage }}
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 送训日期 -->
                    <el-table-column label="送训日期" width="150">
                      <template slot-scope="scope">
                        <el-date-picker
                          v-model="scope.row.flight_start_date"
                          type="date"
                          placeholder="选择日期"
                          size="small"
                          format="yyyy-MM-dd"
                          value-format="yyyy-MM-dd"
                          style="width: 100%">
                        </el-date-picker>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].flight_start_date" class="error-text">
                          {{ validationErrors[scope.$index].flight_start_date }}
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 结训日期 -->
                    <el-table-column label="结训日期" width="150">
                      <template slot-scope="scope">
                        <el-date-picker
                          v-model="scope.row.suspension_date"
                          type="date"
                          placeholder="选择日期"
                          size="small"
                          format="yyyy-MM-dd"
                          value-format="yyyy-MM-dd"
                          style="width: 100%">
                        </el-date-picker>
                      </template>
                    </el-table-column>

                    <!-- 停飞日期 -->
                    <el-table-column label="停飞日期" width="150">
                      <template slot-scope="scope">
                        <el-date-picker
                          v-model="scope.row.stop_flight_date"
                          type="date"
                          placeholder="选择日期"
                          size="small"
                          format="yyyy-MM-dd"
                          value-format="yyyy-MM-dd"
                          style="width: 100%"
                          @change="onStopFlightDateChange(scope.row)">
                        </el-date-picker>
                      </template>
                    </el-table-column>

                    <!-- 停飞原因 -->
                    <el-table-column label="停飞原因" width="140">
                      <template slot-scope="scope">
                        <el-select
                          v-model="scope.row.stop_flight_reason"
                          placeholder="请选择停飞原因"
                          size="small"
                          :class="{'is-error': validationErrors[scope.$index] && validationErrors[scope.$index].stop_flight_reason}"
                          style="width: 100%"
                          clearable>
                          <el-option
                            v-for="reason in stopFlightReasons"
                            :key="reason.value"
                            :label="reason.label"
                            :value="reason.value">
                          </el-option>
                        </el-select>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].stop_flight_reason" class="error-text">
                          {{ validationErrors[scope.$index].stop_flight_reason }}
                        </div>
                      </template>
                    </el-table-column>

                    <!-- 返校毕设时间 -->
                    <el-table-column label="返校毕设时间" width="150">
                      <template slot-scope="scope">
                        <el-date-picker
                          v-model="scope.row.return_school_date"
                          type="date"
                          placeholder="选择日期"
                          size="small"
                          format="yyyy-MM-dd"
                          value-format="yyyy-MM-dd"
                          style="width: 100%">
                        </el-date-picker>
                      </template>
                    </el-table-column>
                    
                    <!-- 安全情况 -->
                    <el-table-column label="安全情况" width="200">
                      <template slot-scope="scope">
                        <el-input
                          v-model="scope.row.safety_situation"
                          type="textarea"
                          :rows="2"
                          placeholder="请填写安全情况"
                          size="small"
                          maxlength="200"
                          show-word-limit>
                        </el-input>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].safety_situation" class="error-text">
                          {{ validationErrors[scope.$index].safety_situation }}
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 不安全事件类型 -->
                    <el-table-column label="不安全事件类型" width="120">
                      <template slot-scope="scope">
                        <el-select
                          v-model="scope.row.safety_event_type"
                          placeholder="请选择不安全事件类型"
                          size="small" style="width: 100%">
                          <el-option
                            v-for="type in safetyEventTypes"
                            :key="type.value"
                            :label="type.label"
                            :value="type.value">
                          </el-option>
                        </el-select>
                      </template>
                    </el-table-column>

                    <!-- 不安全事件原因 -->
                    <el-table-column label="不安全事件原因" width="120">
                      <template slot-scope="scope">
                        <el-select
                          v-model="scope.row.safety_event_reason"
                          placeholder="请选择不安全事件原因"
                          size="small"
                          style="width: 100%">
                          <el-option
                            v-for="reason in safetyEventReasons"
                            :key="reason.value"
                            :label="reason.label"
                            :value="reason.value">
                          </el-option>
                        </el-select>
                      </template>
                    </el-table-column>


                    <!-- 其他情况 -->
                    <el-table-column label="其他情况" width="200">
                      <template slot-scope="scope">
                        <el-input
                          v-model="scope.row.other_situation"
                          type="textarea"
                          :rows="2"
                          placeholder="请填写其他情况"
                          size="small"
                          maxlength="200"
                          show-word-limit>
                        </el-input>
                        <div v-if="validationErrors[scope.$index] && validationErrors[scope.$index].other_situation" class="error-text">
                          {{ validationErrors[scope.$index].other_situation }}
                        </div>
                      </template>
                    </el-table-column>
                    
                    <!-- 操作列 -->
                    <el-table-column label="操作" width="150" fixed="right">
                      <template slot-scope="scope">
                        <el-button 
                          type="text" 
                          size="small" 
                          @click="copyFromPrevious(scope.$index)"
                          :disabled="scope.$index === 0"
                          title="复制上一名学生的通用数据">
                          复制上一行
                        </el-button>
                        <el-button 
                          type="text" 
                          size="small" 
                          icon="el-icon-refresh" 
                          @click="resetStudentForm(scope.$index)"
                          title="重置该学生数据">
                          重置
                        </el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
                
                <!-- 提交按钮区域 -->
                <div class="submit-section">
                  <el-button-group>
                    <el-button type="primary" icon="el-icon-check" @click="submitReport" :loading="submitting" 
                    :disabled="reportSettings && !reportSettings.can_submit"
                    size="medium">提交周报</el-button>
                    <el-button icon="el-icon-refresh" @click="resetAllForms" size="medium">重置所有</el-button>
                  </el-button-group>
                </div>
              </div>
              
              <!-- 空状态 -->
              <div v-else class="empty-state">
                <i class="el-icon-document-add"></i>
                <h3>尚未初始化学生列表</h3>
                <p>请点击"初始化学生列表"按钮开始填写周报</p>
                <el-button type="primary" icon="el-icon-plus" @click="initializeStudentForms" :loading="loadingStudents">初始化学生列表</el-button>
              </div>
            </el-card>
          </div>
        </el-tab-pane>
        
        <!-- 批次学生 -->
        <el-tab-pane label="批次学生" name="students">
          <div class="students-section">
            <el-card shadow="hover">
              <div slot="header" class="card-header">
                <span><i class="el-icon-user"></i> 批次学生列表</span>
                <div>
                  <span class="batch-name">{{ batchStudents.batch_name }}</span>
                  <el-button type="text" icon="el-icon-refresh" @click="loadBatchStudents">刷新</el-button>
                </div>
              </div>
              
              <div v-if="batchStudents.students && batchStudents.students.length > 0">
                <div class="students-summary">
                  <p>共 {{ batchStudents.total_count }} 名学生</p>
                </div>
                
                <el-table :data="batchStudents.students" v-loading="loadingStudents" stripe style="width: 100%">
                  <el-table-column prop="student_number" label="学号" width="120"></el-table-column>
                  <el-table-column prop="name" label="姓名" width="100"></el-table-column>
                  <el-table-column prop="class_id" label="班级" width="100"></el-table-column>
                  <el-table-column prop="training_status" label="训练状态" width="120">
                    <template slot-scope="scope">
                      <el-tag :type="getStatusType(scope.row.training_status)" size="small">{{ scope.row.training_status || '未设置' }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="phone" label="联系电话" show-overflow-tooltip></el-table-column>
                </el-table>
              </div>
              
              <div v-else class="no-students">
                <i class="el-icon-warning"></i>
                <p>未找到该批次的学生信息</p>
              </div>
            </el-card>
          </div>
        </el-tab-pane>
        
        <!-- 历史记录 -->
        <el-tab-pane label="历史" name="history">
          <div class="history-section">
            <div class="section-header">
              <span>周报历史</span>
              <el-button type="text" icon="el-icon-refresh" @click="loadReportHistory" size="small">刷新</el-button>
            </div>
            
            <!-- 桌面端表格 -->
            <el-table 
              v-if="!isMobile"
              :data="reportHistory" 
              v-loading="loadingHistory" 
              stripe 
              size="small"
              style="width: 100%">
              <el-table-column prop="report_week" label="周期" width="120">
                <template slot-scope="scope">
                  <span style="font-size: 14px; font-weight: 600;">{{ scope.row.report_week }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="student_count" label="学生数" width="100">
                <template slot-scope="scope">
                  <el-tag type="info" size="small">{{ scope.row.student_count }}人</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="学生名单" min-width="200">
                <template slot-scope="scope">
                  <div class="student-names" style="font-size: 13px; line-height: 1.4;">
                    <span v-if="scope.row.student_names && scope.row.student_names.length > 0">
                      {{ scope.row.student_names.slice(0, 3).join('、') }}
                      <span v-if="scope.row.student_names.length > 3" style="color: #909399;">等{{ scope.row.student_count }}人</span>
                    </span>
                    <span v-else style="color: #c0c4cc;">暂无数据</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="updated_at" label="更新时间" width="160">
                <template slot-scope="scope">
                  <span style="font-size: 13px;">{{ scope.row.updated_at }}</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="120">
                <template slot-scope="scope">
                  <el-button type="primary" size="mini" icon="el-icon-view" @click="viewReportDetail(scope.row)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
            
            <!-- 移动端列表 -->
            <div v-else class="mobile-history-list">
              <div 
                v-for="report in reportHistory" 
                :key="report.report_id" 
                class="history-item"
                @click="viewReportDetail(report)">
                <div class="history-main">
                  <h4 style="font-size: 16px; margin-bottom: 4px;">{{ report.report_week }}</h4>
                  <div class="student-summary" style="font-size: 14px; color: #606266; margin-bottom: 6px;">
                    <el-tag type="info" size="small">{{ report.student_count }}人</el-tag>
                    <span v-if="report.student_names && report.student_names.length > 0" style="margin-left: 8px;">
                      {{ report.student_names.slice(0, 2).join('、') }}
                      <span v-if="report.student_names.length > 2" style="color: #909399;">等</span>
                    </span>
                  </div>
                  <div class="history-time" style="font-size: 13px;">{{ report.updated_at }}</div>
                </div>
                <i class="el-icon-arrow-right" style="color: #c0c4cc;"></i>
              </div>
            </div>
            
            <!-- 分页 -->
            <div class="pagination-wrapper" v-if="reportHistory.length > 0">
              <el-pagination 
                @size-change="handleSizeChange" 
                @current-change="handlePageChange" 
                :current-page="pagination.page" 
                :page-sizes="[5, 10, 20]" 
                :page-size="pagination.pageSize" 
                :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next'" 
                :total="pagination.total"
                small>
              </el-pagination>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
    
    <!-- 周报详情对话框 -->
    <el-dialog 
      title="周报详情" 
      :visible.sync="detailDialogVisible" 
      :width="isMobile ? '95%' : '80%'" 
      :fullscreen="isMobile"
      :before-close="closeDetailDialog">
      <div v-if="selectedReport" class="report-detail">
        <div class="detail-info">
          <div class="info-item">
            <label>周报周期：</label>
            <span>{{ selectedReport.report_week }}</span>
          </div>
          <div class="info-item">
            <label>学生数量：</label>
            <span>{{ selectedReport.student_count }}</span>
          </div>
          <div class="info-item">
            <label>更新时间：</label>
            <span>{{ selectedReport.updated_at }}</span>
          </div>
        </div>
        
        <div class="student-details-section">
          <h4>学生详情</h4>
          
          <!-- 桌面端表格 -->
          <el-table 
            v-if="!isMobile"
            :data="selectedReport.student_details" 
            stripe 
            size="mini"
            style="width: 100%">
            <el-table-column label="学生信息" width="150">
              <template slot-scope="scope">
                <div class="student-info-cell">
                  <div class="student-name">{{ getStudentName(scope.row.student_number) }}</div>
                  <div class="student-number">{{ scope.row.student_number }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="training_base" label="训练基地" show-overflow-tooltip></el-table-column>
            <el-table-column prop="training_stage" label="训练阶段" width="100"></el-table-column>
            <el-table-column prop="flight_start_date" label="送训日期" width="100"></el-table-column>
            <el-table-column prop="suspension_date" label="结训日期" width="100"></el-table-column>
            <el-table-column prop="stop_flight_date" label="停飞日期" width="100"></el-table-column>
            <el-table-column prop="stop_flight_reason" label="停飞原因" width="100"></el-table-column>
            <el-table-column prop="return_school_date" label="返校毕设时间" width="120"></el-table-column>
            <el-table-column prop="safety_situation" label="安全情况" show-overflow-tooltip></el-table-column>
            <el-table-column prop="safety_event_type" label="不安全事件类型" width="120"></el-table-column>
            <el-table-column prop="safety_event_reason" label="不安全事件原因" width="120"></el-table-column>
            <el-table-column prop="other_situation" label="其他情况" show-overflow-tooltip></el-table-column>
          </el-table>
          
          <!-- 移动端卡片 -->
          <div v-else class="mobile-detail-cards">
            <div 
              v-for="detail in selectedReport.student_details" 
              :key="detail.student_number" 
              class="detail-card">
              <div class="card-header">
                <h4>{{ getStudentName(detail.student_number) }}</h4>
                <span class="student-number-tag">{{ detail.student_number }}</span>
              </div>
              <div class="card-content">
                <div class="detail-row">
                  <label>训练基地：</label>
                  <span>{{ detail.training_base || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>训练阶段：</label>
                  <span>{{ detail.training_stage || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>送训日期：</label>
                  <span>{{ detail.flight_start_date || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>结训日期：</label>
                  <span>{{ detail.suspension_date || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>停飞日期：</label>
                  <span>{{ detail.stop_flight_date || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>停飞原因：</label>
                  <span>{{ detail.stop_flight_reason || '-' }}</span>
                </div>
                <div class="detail-row">
                  <label>返校毕设时间：</label>
                  <span>{{ detail.return_school_date || '-' }}</span>
                </div>
                <div class="detail-row" v-if="detail.safety_situation">
                  <label>安全情况：</label>
                  <span>{{ detail.safety_situation }}</span>
                </div>
                <div class="detail-row" v-if="detail.other_situation">
                  <label>其他情况：</label>
                  <span>{{ detail.other_situation }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeDetailDialog">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from "../../api/index"
import axiosInstance from "../../api/axiosInstance"

export default {
  name: 'WeeklyReport',
  computed: {
    completedStudentCount() {
      return this.studentDetailsForm.filter(student => this.isStudentComplete(student)).length
    },
    incompleteStudentCount() {
      return this.studentDetailsForm.length - this.completedStudentCount
    },
    abnormalStopCount() {
      return this.studentDetailsForm.filter(student => !!student.stop_flight_date).length
    }
  },
  data() {
    return {
      // 不安全事件类型：事故、事故症候、一般事件
      safetyEventTypes: [
        { value: '事故', label: '事故' },
        { value: '事故症候', label: '事故症候' },
        { value: '一般事件', label: '一般事件' }
      ],
      // 不安全事件原因：学生原因、教员原因、管制原因、机械原因、其他原因
      safetyEventReasons: [
        { value: '学生原因', label: '学生原因' },
        { value: '教员原因', label: '教员原因' },
        { value: '管制原因', label: '管制原因' },
        { value: '机械原因', label: '机械原因' },
        { value: '其他原因', label: '其他原因' }
      ],
      stopFlightReasons: [
        { value: '纪律', label: '纪律' },
        { value: '身体', label: '身体' },
        { value: '学习', label: '学习' },
        { value: '技术', label: '技术' }
      ],
      loading: true,
      isLeader: false,
      batchInfo: {},
      activeTab: 'create',
      currentWeek: '',
      studentDetailsForm: [],
      loadingHistory: false,
      submitting: false,
      reportSettings: null,
      trainingStages: [],
      batchStudents: { batch_name: '', school_id: null, students: [], total_count: 0 },
      validationErrors: {}, // 校验错误信息
      reportHistory: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      detailDialogVisible: false,
      selectedReport: null,
      isMobile: false, // 移动端检测
      bulkFill: {
        training_base: '',
        training_stage: '',
        flight_start_date: '',
        safety_situation: '',
        other_situation: ''
      }
    }
  },
  
  async mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    await this.checkPermissionAndInit()
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  
  methods: {
    // 检测移动端
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    
    // 根据学号获取学生姓名
    getStudentName(studentNumber) {
      if (this.batchStudents.students && this.batchStudents.students.length > 0) {
        const student = this.batchStudents.students.find(s => s.student_number === studentNumber)
        return student ? student.name : studentNumber
      }
      return studentNumber
    },
    async checkPermissionAndInit() {
      try {
        this.loading = true
        const permissionResponse = await axiosInstance.get(api.checkLeaderPermission)
        
        if (permissionResponse.data.status === 'success') {
          this.isLeader = true
          this.batchInfo = permissionResponse.data.data
          await Promise.all([this.loadTrainingStages(), this.getCurrentWeek(), this.loadReportHistory(), this.loadReportSettings()])
        } else {
          this.isLeader = false
        }
      } catch (error) {
        this.isLeader = false
        this.$message.error('权限检查失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    
    async loadReportSettings() {
       try {
         const response = await axiosInstance.get(api.getReportSettings('weekly'));
         if (response.data.code === 200) {
           this.reportSettings = response.data.data;
         }
       } catch (error) {
         console.error('加载报告设置失败:', error);
       }
     },
    
    async loadTrainingStages() {
      try {
        const response = await axiosInstance.get(api.getTrainingStages)
        if (response.data.status === 'success') {
          this.trainingStages = response.data.data
        }
      } catch (error) {
        this.$message.error('加载训练阶段失败: ' + error.message)
      }
    },
    
    async getCurrentWeek() {
      try {
        const response = await axiosInstance.get(api.getCurrentWeek)
        if (response.data.status === 'success') {
          this.currentWeek = response.data.data.current_week
        }
      } catch (error) {
        this.$message.error('获取当前周期失败: ' + error.message)
      }
    },
    
    async initializeStudentForms() {
      try {
        this.loadingStudents = true
        await this.loadBatchStudents()
        
        if (this.batchStudents.students && this.batchStudents.students.length > 0) {
          this.studentDetailsForm = this.batchStudents.students.map(student => ({
            student_number: student.student_number,
            name: student.name,
            class_id: student.class_id,
            training_base: '',
            training_stage: '',
            flight_start_date: '',
            suspension_date: '',
            stop_flight_date: '',
            stop_flight_reason: '',
            return_school_date: '',
            safety_situation: '',
            safety_event_type: '',
            safety_event_reason: '',
            other_situation: ''
          }))
          this.$message.success(`已初始化 ${this.studentDetailsForm.length} 名学生的表单`)
        } else {
          this.$message.warning('未找到批次学生，请检查批次配置')
        }
      } catch (error) {
        this.$message.error('初始化学生表单失败: ' + error.message)
      } finally {
        this.loadingStudents = false
      }
    },
    
    async loadBatchStudents() {
      try {
        this.loadingStudents = true
        const response = await axiosInstance.get(api.getBatchStudents)
        if (response.data.status === 'success') {
          this.batchStudents = response.data.data
        } else {
          this.$message.error(response.data.message)
        }
      } catch (error) {
        this.$message.error('加载学生列表失败: ' + error.message)
      } finally {
        this.loadingStudents = false
      }
    },
    
    getStatusType(status) {
      switch(status) {
        case '训练中': return 'success'
        case '训练结束': return 'info'
        case '停飞': return 'warning'
        case '待送训': return 'primary'
        default: return ''
      }
    },

    isStudentComplete(student) {
      const requiredFilled = Boolean(
        student.training_base && student.training_base.trim() &&
        student.training_stage && student.training_stage.trim() &&
        student.flight_start_date &&
        student.safety_situation && student.safety_situation.trim() &&
        student.other_situation && student.other_situation.trim()
      )

      if (!requiredFilled) {
        return false
      }

      if (student.stop_flight_date && (!student.stop_flight_reason || !student.stop_flight_reason.trim())) {
        return false
      }

      return true
    },

    applyBulkValues(mode) {
      const hasValue =
        this.bulkFill.training_base ||
        this.bulkFill.training_stage ||
        this.bulkFill.flight_start_date ||
        this.bulkFill.safety_situation ||
        this.bulkFill.other_situation
      if (!hasValue) {
        this.$message.warning('请先填写至少一个快捷值')
        return
      }

      this.studentDetailsForm.forEach(student => {
        const shouldFillBase = this.bulkFill.training_base && (mode === 'all' || !student.training_base)
        const shouldFillStage = this.bulkFill.training_stage && (mode === 'all' || !student.training_stage)
        const shouldFillStartDate = this.bulkFill.flight_start_date && (mode === 'all' || !student.flight_start_date)
        const shouldFillSafety = this.bulkFill.safety_situation && (mode === 'all' || !student.safety_situation)
        const shouldFillOther = this.bulkFill.other_situation && (mode === 'all' || !student.other_situation)

        if (shouldFillBase) {
          student.training_base = this.bulkFill.training_base
        }

        if (shouldFillStage) {
          student.training_stage = this.bulkFill.training_stage
        }

        if (shouldFillStartDate) {
          student.flight_start_date = this.bulkFill.flight_start_date
        }

        if (shouldFillSafety) {
          student.safety_situation = this.bulkFill.safety_situation
        }

        if (shouldFillOther) {
          student.other_situation = this.bulkFill.other_situation
        }
      })

      this.validationErrors = {}
      this.$message.success(mode === 'all' ? '已覆盖批量字段' : '已填充空白项')
    },

    resetBulkFill() {
      this.bulkFill = {
        training_base: '',
        training_stage: '',
        flight_start_date: '',
        safety_situation: '',
        other_situation: ''
      }
    },

    copyFromPrevious(index) {
      if (index === 0) {
        return
      }

      const previous = this.studentDetailsForm[index - 1]
      const current = this.studentDetailsForm[index]
      const copyFields = [
        'training_base',
        'training_stage',
        'flight_start_date',
        'suspension_date',
        'stop_flight_date',
        'stop_flight_reason',
        'return_school_date',
        'safety_situation',
        'safety_event_type',
        'safety_event_reason',
        'other_situation'
      ]

      copyFields.forEach(field => {
        current[field] = previous[field]
      })

      this.$delete(this.validationErrors, index)
      this.$message.success(`已复制 ${previous.name || '上一行'} 的填写内容`)
    },
    
    async loadLastWeekData() {
      try {
        this.loadingHistory = true
        const response = await axiosInstance.get(api.getLastWeekReport)
        
        if (response.data.status === 'success' && response.data.data) {
          const lastData = response.data.data
          
          if (lastData.student_details && lastData.student_details.length > 0) {
            if (this.studentDetailsForm.length === 0) {
              await this.initializeStudentForms()
            }
            
            lastData.student_details.forEach(lastStudent => {
              const currentStudent = this.studentDetailsForm.find(s => s.student_number === lastStudent.student_number)
              if (currentStudent) {
                currentStudent.training_base = lastStudent.training_base || ''
                currentStudent.training_stage = lastStudent.training_stage || ''
                currentStudent.flight_start_date = lastStudent.flight_start_date || ''
                currentStudent.suspension_date = lastStudent.suspension_date || ''
                currentStudent.stop_flight_date = lastStudent.stop_flight_date || ''
                currentStudent.stop_flight_reason = lastStudent.stop_flight_reason || ''
                currentStudent.return_school_date = lastStudent.return_school_date || ''
                currentStudent.safety_situation = lastStudent.safety_situation || ''
                currentStudent.other_situation = lastStudent.other_situation || ''
                currentStudent.safety_event_type = lastStudent.safety_event_type || ''
                currentStudent.safety_event_reason = lastStudent.safety_event_reason || ''
              }
            })
            
            // 清除校验错误
            this.validationErrors = {}
            
            this.$message.success('已复制上期数据，请根据需要修改')
          } else {
            this.$message.info('上期周报无学生数据')
          }
        } else {
          this.$message.info('暂无历史数据可复制')
        }
      } catch (error) {
        this.$message.error('加载历史数据失败: ' + error.message)
      } finally {
        this.loadingHistory = false
      }
    },
    
    // 校验周报数据
    validateReportData() {
      this.validationErrors = {}
      let hasError = false
      
      this.studentDetailsForm.forEach((student, index) => {
        const errors = {}
        
        // 校验训练基地（必填）
        if (!student.training_base || student.training_base.trim() === '') {
          errors.training_base = '训练基地为必填项'
          hasError = true
        }
        
        // 校验训练阶段（必填）
        if (!student.training_stage || student.training_stage.trim() === '') {
          errors.training_stage = '训练阶段为必填项'
          hasError = true
        }

        if (!student.flight_start_date) {
          errors.flight_start_date = '送训日期为必填项'
          hasError = true
        }

        if (!student.safety_situation || student.safety_situation.trim() === '') {
          errors.safety_situation = '安全情况为必填项'
          hasError = true
        }

        if (!student.other_situation || student.other_situation.trim() === '') {
          errors.other_situation = '其他情况为必填项'
          hasError = true
        }

        if (student.stop_flight_date && (!student.stop_flight_reason || student.stop_flight_reason.trim() === '')) {
          errors.stop_flight_reason = '填写停飞日期后，停飞原因为必填项'
          hasError = true
        }
        
        // 如果有错误，记录到validationErrors中
        if (Object.keys(errors).length > 0) {
          this.$set(this.validationErrors, index, errors)
        }
      })
      
      return !hasError
    },

    onStopFlightDateChange(student) {
      if (student.stop_flight_date) {
        student.suspension_date = student.stop_flight_date
      }
    },
    
    // 重置单个学生表单
    resetStudentForm(index) {
      this.$confirm('确定要重置该学生的表单数据吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const student = this.studentDetailsForm[index]
        student.training_base = ''
        student.training_stage = ''
        student.flight_start_date = ''
        student.suspension_date = ''
        student.stop_flight_date = ''
        student.stop_flight_reason = ''
        student.return_school_date = ''
        student.safety_situation = ''
        student.safety_event_type = ''
        student.safety_event_reason = ''
        student.other_situation = ''
        
        // 清除该学生的校验错误
        this.$delete(this.validationErrors, index)
        
        this.$message.success('已重置该学生的表单数据')
      }).catch(() => {})
    },
    
    async submitReport() {
      try {
        if (this.studentDetailsForm.length === 0) {
          this.$message.warning('请先初始化学生列表')
          return
        }
        
        // 校验表单数据
        if (!this.validateReportData()) {
          this.$message.error('请检查并修正必填项的错误')
          return
        }
        
        // 二次确认
        await this.$confirm(
          `确定要提交 ${this.currentWeek} 周报吗？\n包含 ${this.studentDetailsForm.length} 名学生的数据。`, 
          '确认提交', 
          {
            confirmButtonText: '确定提交',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        this.submitting = true
        const reportData = { report_week: this.currentWeek, student_details: this.studentDetailsForm }
        const response = await axiosInstance.post(api.submitWeeklyReport, reportData)
        
        if (response.data.status === 'success') {
          this.$message.success('周报提交成功')
          this.validationErrors = {} // 清除校验错误
          this.loadReportHistory()
        } else {
          this.$message.error(response.data.message)
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('提交失败: ' + error.message)
        }
      } finally {
        this.submitting = false
      }
    },
    
    resetAllForms() {
      this.$confirm('确定要重置所有学生的表单数据吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.studentDetailsForm.forEach(student => {
          student.training_base = ''
          student.training_stage = ''
          student.flight_start_date = ''
          student.suspension_date = ''
          student.stop_flight_date = ''
          student.stop_flight_reason = ''
          student.return_school_date = ''
          student.safety_situation = ''
          student.safety_event_type = ''
          student.safety_event_reason = ''
          student.other_situation = ''
        })
        
        // 清除所有校验错误
        this.validationErrors = {}
        
        this.$message.success('已重置所有表单')
      }).catch(() => {})
    },
    
    async loadReportHistory() {
      try {
        this.loadingHistory = true
        const params = { page: this.pagination.page, page_size: this.pagination.pageSize }
        const response = await axiosInstance.get(api.getWeeklyReports, { params })
        
        if (response.data.status === 'success') {
          this.reportHistory = response.data.data.items
          this.pagination.total = response.data.data.total
          
          // 为每个周报获取学生姓名
          for (let report of this.reportHistory) {
            try {
              const detailParams = { report_id: report.report_id }
              const detailResponse = await axiosInstance.get(api.getWeeklyReportDetail, { params: detailParams })
              
              if (detailResponse.data.status === 'success' && detailResponse.data.data.student_details) {
                // 提取学生姓名，先从 student_details 中找到对应的学生信息
                const studentNames = []
                const studentNumbers = detailResponse.data.data.student_details.map(detail => detail.student_number)
                
                // 从批次学生中查找对应的姓名
                if (this.batchStudents.students && this.batchStudents.students.length > 0) {
                  for (let studentNumber of studentNumbers) {
                    const student = this.batchStudents.students.find(s => s.student_number === studentNumber)
                    if (student) {
                      studentNames.push(student.name)
                    } else {
                      studentNames.push(studentNumber) // 如果找不到姓名，使用学号
                    }
                  }
                } else {
                  // 如果没有批次学生数据，就使用学号
                  studentNames.push(...studentNumbers)
                }
                
                report.student_names = studentNames
              } else {
                report.student_names = []
              }
            } catch (error) {
              console.warn(`获取周报 ${report.report_id} 的学生信息失败:`, error)
              report.student_names = []
            }
          }
        }
      } catch (error) {
        this.$message.error('加载历史记录失败: ' + error.message)
      } finally {
        this.loadingHistory = false
      }
    },
    
    async viewReportDetail(report) {
      try {
        // 确保有批次学生数据
        if (this.batchStudents.students.length === 0) {
          await this.loadBatchStudents()
        }
        
        const params = { report_id: report.report_id }
        const response = await axiosInstance.get(api.getWeeklyReportDetail, { params })
        
        if (response.data.status === 'success') {
          this.selectedReport = response.data.data
          this.detailDialogVisible = true
        }
      } catch (error) {
        this.$message.error('获取报告详情失败: ' + error.message)
      }
    },
    
    closeDetailDialog() {
      this.detailDialogVisible = false
      this.selectedReport = null
    },
    
    handleTabClick(tab) {
      if (tab.name === 'history' && this.reportHistory.length === 0) {
        // 确保先加载批次学生信息，然后加载历史记录
        if (this.batchStudents.students.length === 0) {
          this.loadBatchStudents().then(() => {
            this.loadReportHistory()
          })
        } else {
          this.loadReportHistory()
        }
      } else if (tab.name === 'students' && this.batchStudents.students.length === 0) {
        this.loadBatchStudents()
      }
    },
    
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      // 确保有批次学生数据后再加载历史
      if (this.batchStudents.students.length === 0) {
        this.loadBatchStudents().then(() => {
          this.loadReportHistory()
        })
      } else {
        this.loadReportHistory()
      }
    },
    
    handlePageChange(page) {
      this.pagination.page = page
      // 确保有批次学生数据后再加载历史
      if (this.batchStudents.students.length === 0) {
        this.loadBatchStudents().then(() => {
          this.loadReportHistory()
        })
      } else {
        this.loadReportHistory()
      }
    },
    
    goBack() {
      this.$router.go(-1)
    },
    
    // 跳转到月报查看页面
    goToMonthlyReportView() {
      this.$router.push('/monthly_report_view')
    }
  }
}
</script>

<style scoped>
/* 基础布局 */
.weekly-report-container { 
  padding: 20px; 
  min-height: calc(100vh - 120px); 
  background-color: #f5f7fa; 
}

.loading-container { 
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  justify-content: center; 
  height: 300px; 
  color: #909399; 
}
.loading-container i { font-size: 40px; margin-bottom: 20px; color: #409EFF; }

.no-permission { 
  text-align: center; 
  padding: 50px 0; 
  color: #606266; 
}
.no-permission i { font-size: 60px; color: #E6A23C; margin-bottom: 20px; }
.no-permission h3 { margin: 15px 0; color: #303133; }

/* 页面头部 */
.page-header { 
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px; 
  padding-bottom: 15px; 
  border-bottom: 1px solid #ebeef5; 
}
.header-content h2 { 
  margin: 0 0 8px 0; 
  color: #303133; 
  display: flex; 
  align-items: center; 
  gap: 8px;
  font-size: 18px;
}
.batch-info { 
  margin: 0; 
  color: #606266; 
  font-size: 16px; 
  font-weight: 500;
}
.header-actions {
  display: flex;
  gap: 10px;
}

/* 标签页 */
.mobile-tabs {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 移动端操作区 */
.mobile-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.week-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #303133;
}
.action-buttons {
  display: flex;
  gap: 8px;
}

/* 学生表单头部 */
.student-forms-header { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  margin-bottom: 15px; 
  padding: 12px 16px; 
  background-color: #f0f9ff; 
  border-radius: 6px; 
  border-left: 4px solid #409eff; 
}
.student-count { 
  margin: 0; 
  color: #409eff; 
  font-weight: 600;
  font-size: 14px;
}

.quick-overview {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.overview-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e4eef8;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.overview-card strong {
  font-size: 22px;
  color: #1f2d3d;
}

.overview-label {
  font-size: 12px;
  color: #7a869a;
}

.overview-card.warning {
  background: linear-gradient(135deg, #fffaf0 0%, #fff4da 100%);
  border-color: #f6d28b;
}

.overview-card.danger {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe2e2 100%);
  border-color: #f5b4b4;
}

.quick-fill-panel {
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #e7eef7;
  border-radius: 10px;
  background: linear-gradient(180deg, #fbfdff 0%, #f4f8fc 100%);
}

.quick-fill-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  color: #2c3e50;
  font-weight: 600;
}

.quick-fill-tip {
  font-size: 12px;
  font-weight: 400;
  color: #7f8c9a;
}

.quick-fill-body {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr 1.3fr 1.3fr auto;
  gap: 10px;
  align-items: start;
}

.quick-fill-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}

/* 桌面端表格 */
.desktop-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 学生信息单元格 */
.student-info-cell {
  padding: 5px 0;
}
.student-name {
  font-weight: bold;
  color: #303133;
  font-size: 13px;
  margin-bottom: 2px;
}
.student-number {
  color: #909399;
  font-size: 11px;
}

/* 错误样式 */
.is-error .el-input__inner,
.is-error .el-textarea__inner {
  border-color: #f56c6c !important;
}
.is-error .el-select .el-input__inner {
  border-color: #f56c6c !important;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .quick-overview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .quick-fill-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .quick-fill-body {
    grid-template-columns: 1fr;
  }

  .quick-fill-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
.error-text {
  color: #f56c6c;
  font-size: 11px;
  margin-top: 2px;
  line-height: 1.2;
}

/* 移动端卡片列表 */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.student-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
}
.student-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f7fa;
}
.student-info h4 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}
.student-info .student-number {
  color: #909399;
  font-size: 12px;
}

/* 表单行 */
.card-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.form-row label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}
.form-row-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.required {
  color: #f56c6c;
}

/* 底部操作区 */
.bottom-actions { 
  text-align: center; 
  padding: 20px; 
  background: white;
  border-radius: 8px;
  margin-top: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* 空状态 */
.empty-state { 
  text-align: center; 
  padding: 60px 20px; 
  color: #909399;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.empty-state i { font-size: 48px; margin-bottom: 15px; color: #dcdfe6; }
.empty-state h4 { margin: 15px 0 8px; color: #303133; }

/* 区块头部 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 移动端学生列表 */
.mobile-student-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.student-item {
  background: white;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
}
.student-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.student-main h4 {
  margin: 0;
  font-size: 14px;
  color: #303133;
}
.student-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.class-info {
  font-size: 12px;
  color: #909399;
}
.student-phone {
  font-size: 12px;
  color: #606266;
}

/* 移动端历史列表 */
.mobile-history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.history-item {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
}
.history-item:active {
  background-color: #f5f7fa;
  transform: scale(0.98);
}
.history-main {
  flex: 1;
}
.history-main h4 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  font-weight: 600;
}
.student-summary {
  display: flex;
  align-items: center;
  margin: 6px 0;
}
.history-time {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

/* 桌面端表格优化 */
.student-names {
  line-height: 1.5;
}

/* 对话框标题优化 */
.detail-card .card-header h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

/* 对话框详情 */
.detail-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
}
.info-item {
  display: flex;
  gap: 8px;
}
.info-item label {
  font-weight: 600;
  color: #606266;
  min-width: 80px;
}

/* 移动端详情卡片 */
.mobile-detail-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.detail-card {
  background: #f9f9f9;
  border-radius: 6px;
  padding: 12px;
  border: 1px solid #ebeef5;
}
.detail-card .card-header h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 14px;
}
.detail-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.student-number-tag {
  background: #e6f7ff;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}
.detail-row {
  display: flex;
  margin-bottom: 4px;
}
.detail-row label {
  font-weight: 500;
  color: #606266;
  min-width: 80px;
  font-size: 12px;
}
.detail-row span {
  color: #303133;
  font-size: 12px;
}

.no-students { 
  text-align: center; 
  padding: 40px 20px; 
  color: #909399;
  background: white;
  border-radius: 8px;
}
.no-students i { font-size: 32px; margin-bottom: 10px; color: #dcdfe6; }

.pagination-wrapper { 
  margin-top: 15px; 
  text-align: center; 
}

.student-details-section { 
  margin-top: 16px; 
}
.student-details-section h4 { 
  margin: 16px 0 8px; 
  color: #303133; 
  font-weight: 600;
  font-size: 14px;
}

/* 移动端隐藏文本 */
@media (max-width: 768px) {
  .mobile-hidden {
    display: none;
  }
  
  .weekly-report-container { 
    padding: 8px; 
  }
  
  .page-header { 
    padding: 12px; 
    margin-bottom: 10px;
  }
  
  .header-content h2 { 
    font-size: 16px; 
  }
  
  .student-card {
    padding: 12px;
  }
  
  .form-row-group {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .bottom-actions {
    padding: 15px;
    flex-direction: column;
    gap: 8px;
  }
  
  .bottom-actions .el-button {
    width: 100%;
  }
}

/* 超小屏幕 */
@media (max-width: 480px) {
  .quick-overview {
    grid-template-columns: 1fr;
  }

  .weekly-report-container {
    padding: 5px;
  }
  
  .mobile-actions {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .history-item {
    padding: 14px;
  }
  
  .history-main h4 {
    font-size: 15px;
  }
  
  .student-summary {
    font-size: 13px;
  }
}
</style>
